/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { FC, useEffect } from 'react';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';

export const TAB_ANIMATION_STAGGER_DELAY_MS = 100;

export interface AnimatedTabIconProps {
  url?: string;
  index: number;
  isDesktop: boolean;
  size?: number;
}

export const AnimatedTabIcon: FC<AnimatedTabIconProps> = ({
  url,
  index,
  isDesktop,
  size = 40,
}) => {
  const opacity = useSharedValue(0);
  const scale = useSharedValue(0);

  useEffect(() => {
    if (isDesktop) {
      opacity.value = 0;
      scale.value = 0;

      const delay = index * TAB_ANIMATION_STAGGER_DELAY_MS;

      opacity.value = withDelay(
        delay,
        withTiming(1, {
          duration: 600,
          easing: Easing.out(Easing.ease),
        }),
      );

      scale.value = withDelay(
        delay,
        withSequence(
          withTiming(1.15, {
            duration: 300,
            easing: Easing.out(Easing.cubic),
          }),
          withTiming(0.85, {
            duration: 150,
            easing: Easing.inOut(Easing.ease),
          }),
          withTiming(1.05, {
            duration: 100,
            easing: Easing.inOut(Easing.ease),
          }),
          withTiming(1, {
            duration: 50,
            easing: Easing.out(Easing.ease),
          }),
        ),
      );
    }
  }, [isDesktop, index, opacity, scale]);

  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: opacity.value,
      transform: [{ scale: scale.value }],
    }),
    [opacity, scale],
  );

  if (!url) return null;

  return (
    <Animated.View
      style={[styles.container, { width: size, height: size }, animatedStyle]}
    >
      <Animated.Image
        testID="animated-tab-icon-image"
        source={{ uri: url }}
        style={{ width: size, height: size }}
        resizeMode="contain"
      />
    </Animated.View>
  );
};

export default AnimatedTabIcon;
const styles = StyleSheet.create(() => ({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0,
    transform: [{ scale: 0 }],
  },
}));
